/* global setInterval, clearInterval */

/**
 * @file MiniAppBridge.js
 * @description
 * Headless bridge between the React app and the native shell of the desktop mini
 * apps (see `src-tauri/src/miniapp.rs`). Renders nothing and does nothing in the
 * full Happy App, on mobile, or in a plain browser.
 *
 *   Happy Time Tracker → the running timer in the menu bar / tray
 *                        (`● 1:05`, `❚❚ 0:20`; cleared when idle).
 *   Happy Team Chat    → the unread total on the Dock / taskbar badge, plus a
 *                        native notification when it grows while the window is
 *                        in the background.
 *
 * Mounted once inside `TeamChatProvider` (see `AppProviders`) because the chat
 * half reads the hub badge from that context.
 */

import {useCallback, useEffect, useRef} from 'react';
import {useTranslation} from 'react-i18next';

import {getMiniApp} from '../../config/appVariant';
import {getAccessToken} from '../../helpers/storage';
import {fetchDailyTasks, fetchTrackerState} from '../../helpers/timeTrackingApi';
import {useChatHubBadge} from '../../helpers/teamChat/useChatHubBadge';
import {
    TIMER_SYNC_MS,
    TRAY_REFRESH_MS,
    buildTrayTitle,
    shouldNotifyUnread,
} from '../../helpers/miniAppBridge';
import {isDesktopShell, isWindowFocused, notifyNative, setDockBadge, setTrayTitle} from '../../tauri/miniAppNative';

/** Time Tracker half: server timer → tray text, overdue daily tasks → dock badge. */
const TimerTrayBridge = () => {
    const timerRef = useRef(null);

    const publish = useCallback(() => {
        setTrayTitle(buildTrayTitle(timerRef.current));
    }, []);

    const sync = useCallback(async () => {
        if (!(await getAccessToken())) {
            timerRef.current = null;
            publish();
            setDockBadge(0);

            return;
        }
        const state = await fetchTrackerState();
        // A failed read is NOT "idle" — keep the last known clock instead of blanking it.
        if (state.ok) {
            timerRef.current = state.timer;
            publish();
        }
        // Dock badge = overdue "Today's tasks". Only a successful read moves it;
        // an older server (unsupported) leaves it cleared.
        const daily = await fetchDailyTasks();
        if (daily.ok || daily.unsupported) {
            setDockBadge(daily.counts.overdue);
        }
    }, [publish]);

    useEffect(() => {
        sync();
        const syncId = setInterval(sync, TIMER_SYNC_MS);
        const tickId = setInterval(publish, TRAY_REFRESH_MS);
        const onFocus = () => sync();
        window.addEventListener('focus', onFocus);

        return () => {
            clearInterval(syncId);
            clearInterval(tickId);
            window.removeEventListener('focus', onFocus);
            setTrayTitle(null);
            setDockBadge(0);
        };
    }, [publish, sync]);

    return null;
};

/** Team Chat half: unread total → dock / taskbar badge + background notification. */
const ChatBadgeBridge = () => {
    const {t} = useTranslation();
    const {visible, total} = useChatHubBadge();
    const previousRef = useRef(null);

    useEffect(() => {
        if (!visible) {
            return;
        }
        setDockBadge(total);
        if (shouldNotifyUnread({previous: previousRef.current, next: total, focused: isWindowFocused()})) {
            notifyNative(getMiniApp()?.name || 'Happy Chat', t('team_chat_unread_count', {count: total}));
        }
        previousRef.current = total;
    }, [visible, total, t]);

    useEffect(() => () => {
        setDockBadge(0);
    }, []);

    return null;
};

/**
 * @returns {JSX.Element|null} The bridge for the active mini app, or null.
 */
const MiniAppBridge = () => {
    const miniApp = getMiniApp();

    if (!miniApp || !isDesktopShell()) {
        return null;
    }

    return miniApp.id === 'teamchat' ? <ChatBadgeBridge /> : <TimerTrayBridge />;
};

export default MiniAppBridge;
